import {
    ComponentType,
    createContext,
    Dispatch,
    FC,
    ForwardedRef,
    forwardRef,
    RefObject,
    SetStateAction,
    useContext,
    useState,
} from 'react';
import { noop } from 'lodash';
// Import
import { MenuItemDropdownRef } from './types';

interface Refs {
    [depthLevel: string]: {
        [index: string]: RefObject<HTMLDivElement>;
    };
}

interface MenuItemContentRefsContext {
    refs: Refs;
    setRefs: Dispatch<SetStateAction<Refs>>;
    isContextProviderMounted: boolean;
}

const menuItemContentRefsContext = createContext<MenuItemContentRefsContext>({
    refs: {},
    setRefs: noop,
    isContextProviderMounted: false,
});

export const useMenuItemContentRefs = () => useContext(menuItemContentRefsContext);

export const MenuItemContentRefsProvider: FC = ({ children }) => {
    const [refs, setRefs] = useState<MenuItemContentRefsContext['refs']>({});
    return (
        <menuItemContentRefsContext.Provider value={{ refs, setRefs, isContextProviderMounted: true }}>
            {children}
        </menuItemContentRefsContext.Provider>
    );
};

export const MenuItemContentRefsHOC = <P extends object>(Component: ComponentType<P>) => {
    const WrappedComponent = (props: P, ref: ForwardedRef<MenuItemDropdownRef>) => {
        const context = useMenuItemContentRefs();

        return context.isContextProviderMounted ? (
            <Component ref={ref} {...props} />
        ) : (
            <MenuItemContentRefsProvider>{<Component ref={ref} {...props} />}</MenuItemContentRefsProvider>
        );
    };

    WrappedComponent.displayName = `MenuItemContentRefsHOC(${Component.displayName || Component.name || 'Component'})`;

    return forwardRef<MenuItemDropdownRef, P>(WrappedComponent);
};
